<template>
<!--
    Job Autocomplete Component

    Lists Request entities (the umbrella "Job"), grouped with their child
    WorkOrder(Title='Request') tickets underneath. Selecting a job header
    sets the parent Request Id and clears the work-order ticket. Selecting a
    ticket row sets both. The "no tickets" placeholder rows are informational
    only and cannot be selected.

    @version 2.0
-->

<div class="input-group w-100 flex-nowrap border-0">

    <!-- job auto complete -->
    <kt-autocomplete
        class="flex-grow-1"
        :inputclass="props.floatingLabel ? 'form-control rounded-end-0' : 'form-control rounded-end-0 form-control-sm'"
        :options="displayOptions"
        :display-by="rowDisplayLabel"
        :value-by="rowValueKey"
        :disable-option="isRowNotSelectable"
        :label="props.label"
        :floating-label="props.floatingLabel"
        :placeholder="props.placeholder"
        @change="onRowChange"
        @update="onSearchUpdate"
        @disabled-click="onDisabledRowClick"
        v-model="acSelection"
        suffix-class="p-0"
        :min-width="420"
        :disabled="props.disabled"
    >

    <template #option="{ option }">
        <div
            v-if="option.RowType === 'header'"
            class="dropdown-item autocomplete-option jobac-row jobac-row-header"
        >
            <strong>#{{ option.job.Id }}</strong>
            <span class="ms-1">{{ headerLabel(option.job) }}</span>
        </div>

        <div
            v-else-if="option.RowType === 'ticket'"
            class="dropdown-item autocomplete-option jobac-row jobac-row-ticket"
        >
            <span
                class="jobac-status-dot me-2"
                :class="ticketStatusBgClass(option.ticket.Status)"
            ></span>
            <div class="jobac-ticket-text">
                <div class="jobac-ticket-title">
                    #{{ option.ticket.Id }} - {{ option.ticket.RequestTitle || option.ticket.Description || "No title" }}
                </div>
                <div class="jobac-ticket-sub text-muted">
                    {{ ticketStatusLabel(option.ticket.Status) }}
                    <span v-if="option.ticket.Priority"> &middot; {{ option.ticket.Priority }}</span>
                </div>
            </div>
        </div>

        <div
            v-else
            class="dropdown-item autocomplete-option jobac-row jobac-row-empty text-muted"
        >
            <em>No linked WorkOrder tickets - cannot log time</em>
        </div>
    </template>

    <template #suffix>

        <!-- filter jobs -->
        <kt-multi-select
            css="rounded-0"
            placeholder="<i class='fa fa-filter'></i>"
            :options="statusOptions"
            labelKey="Status"
            valueKey="Id"
            v-model="statusFilter"
        >
        </kt-multi-select>

        <!-- edit request ticket button -->
        <button 
            v-if="model"
            @click.stop.prevent="editJob(model)" 
            class="btn btn-sm btn-outline-secondary rounded-start-0 rounded-end-0"
            type="button"
            title="Edit Request"
        ><i class="fa fa-edit"></i>
        </button>

        <!-- new job button - hidden: prevent creating work orders from this selector -->
        <!--
        <button 
            @click.stop.prevent="editJob()"
            class="btn btn-sm btn-outline-primary rounded-start-0"
            type="button"
        ><i class="fa fa-plus"></i>
        </button>
        -->
    </template>

    </kt-autocomplete>

</div>

<kt-modal
    :title="formTitle"
    :visible="formVisible"
    :size="'modal-xl'"
    @dismiss="formDismiss()"
>
    <template #body>
        <pm-form
            v-if="isPM"
            v-model="pmForm"
        ></pm-form>
        <request-form
            v-else
            v-model="form"
        ></request-form>
    </template>

    <template #footer>
        <div class="-container-fluid -px-0 kt-modal-actions">
            <button 
                @click="formDismiss()" 
                class="btn btn-outline-secondary -form-control"
            ><X :size="16" class="me-1" />Close</button>
            <button 
                @click="formSave(form)" 
                class="btn btn-outline-primary -form-control"
            ><Save :size="16" class="me-1" />Save</button>
        </div>
    </template>
</kt-modal>

</template>

<script setup>

import { ref, watch, onUnmounted, onMounted, computed } from "vue";
import { useJobStatusStore } from "@/stores/JobStatusStore";
import { storeToRefs } from "pinia";

import Api from "@/services/Api";
import ApiService from "@/services/ApiService";
import OptionsService from "@/services/OptionsService";
import Toast from "@/services/Toast";
import Emitter from "@/services/Emitter";
import { usePermissions } from "@/composables/usePermissions";
import KtAutocomplete from "@/components/UI/KtAutocomplete.vue";
import KtMultiSelect from "@/components/UI/KtMultiSelect.vue";
import KtModal from "@/components/UI/KtModal.vue";
import RequestForm from "@/components/Requests/RequestForm.vue";
import PmForm from "@/components/PMs/PMForm.vue";
import { X, Save } from 'lucide-vue-next';

const model = defineModel({
    type: String,
    default: "",
});

// The selected WorkOrder(Title='Request') ticket's Id - the specific record
// techs must pick before time can be logged. Kept separate from `model`
// (which stays the parent Request/Job Id) for backward compatibility with
// existing JobAc consumers.
const workOrderId = defineModel("workOrderId", {
    type: [Number, String],
    default: null,
});

const props = defineProps({ 
    "rowkey": {
        type: String,
        default: "Id"
    },

    "label": {
        type: String,
        default: "Select job"
    },

    "floatingLabel": {
        type: String,
        default: ""
    },
    
    "client": {
        type: String,
        default: ""
    },
    
    "inputClass": {
        type: String,
        default: ""
    },
    
    "placeholder": {
        type: String,
        default: "Choose client then select a job"
    },
    
    "pmOnly": {
        type: Boolean,
        default: false
    },

    "disabled": {
        type: Boolean,
        default: false
    },
    
    // deprecated ...
    "status": {
        type: Array,
        default: []
    },
});

const emits = defineEmits(["change", "status-change", "workorder-change"]);
const jobStatusStore = useJobStatusStore();
const { userId } = usePermissions();
const JOBAC_FILTER_KEY = 'jobac_status_filter';

const { list: statusList } = storeToRefs(jobStatusStore);

// Convert the Record to an array for kt-multi-select
const statusOptions = computed(() => Object.values(statusList.value));

const statusFilter = ref([]);
let filterInitialized = false;
let statusWatchCount = 0;
let statusFilterWatchCount = 0;

// Normalize status prop to always be an array of numbers
const normalizeStatus = (status) => {
    if (!status) return [];
    if (Array.isArray(status)) {
        return status.map(s => Number(s));
    }
    // Handle single value (number or string)
    return [Number(status)];
};

// Sync statusFilter when status prop changes (only if not yet initialized from saved prefs)
watch(() => props.status, (newStatus) => {
    statusWatchCount++;
    if (statusWatchCount % 50 === 0) {
        console.warn(`⚠️ JobAc props.status watcher fired ${statusWatchCount} times! INFINITE LOOP!`, newStatus);
    }
    console.debug(`🔄 JobAc props.status watch (call #${statusWatchCount}):`, newStatus, 'filterInitialized:', filterInitialized);
    if (filterInitialized) return;
    const normalized = normalizeStatus(newStatus);
    if (normalized.length > 0) {
        statusFilter.value = normalized;
    }
}, { immediate: true });

const list = ref([]);
const form = ref({});
const pmForm = ref({});
const isPM = ref(false);
const formTitle = ref("New Job");
const formVisible = ref(false);
const isLoadingJobs = ref(false);

// Internal selection driving the kt-autocomplete widget. This tracks a
// flattened row key (see groupedOptions below), NOT the public `model`
// (which stays the parent Request Id for backward compatibility).
const acSelection = ref("");
const lastValidSelection = ref("");
const searchText = ref("");

const unwatch = [];

// WorkOrder(Title='Request') ticket status -> label/color. Mirrors the
// mapping used in WorkOrderDatatable.vue's getStatusLabel/getStatusBadgeClass.
const TICKET_STATUS_LABELS = {
    0: "Draft",
    1: "Pending",
    2: "Approved",
    3: "Complete",
    4: "Cancelled",
    5: "Finalized",
};

const TICKET_STATUS_BG_CLASSES = {
    0: "bg-secondary",
    1: "bg-warning",
    2: "bg-info",
    3: "bg-success",
    4: "bg-danger",
    5: "bg-primary",
};

const ticketStatusLabel = (status) => TICKET_STATUS_LABELS[status] ?? "Unknown";
const ticketStatusBgClass = (status) => TICKET_STATUS_BG_CLASSES[status] ?? "bg-secondary";

const headerLabel = (row) => {
    // format should be STATUS - JobId - Description
    let name = row.RequestDetailedDescription 
        || row.TaskDetail
        || "Description Not Set";

    for (let i = 0; i < jobStatusStore.list.length; i++) {
        let tmp = jobStatusStore.list[i];
        if (tmp.Id === row.RequestStatus) {
            name = tmp.Status + " - " + name;
            break;
        }
    }

    let id = row.Id;
    name = id + " | " + name;

    return name;
};

// Computed property to filter list based on status. The currently selected
// job is always included so the component can display it even if the user's
// status filter would otherwise hide that status.
const filteredList = computed(() => {
    if (!statusFilter.value || statusFilter.value.length === 0) {
        return list.value;
    }
    return list.value.filter(job => 
        statusFilter.value.includes(job.RequestStatus)
        || String(job.Id) === String(model.value)
    );
});

// Flatten each Request into a header row + one row per linked
// WorkOrder(Title='Request') ticket (or a non-selectable placeholder row
// when there are no linked tickets yet).
const groupedOptions = computed(() => {
    const rows = [];

    for (const job of filteredList.value) {
        rows.push({ RowType: "header", RowKey: "h-" + job.Id, job });

        const tickets = job.RequestTickets || [];

        if (tickets.length > 0) {
            for (const ticket of tickets) {
                rows.push({ RowType: "ticket", RowKey: "t-" + ticket.Id, job, ticket });
            }
        } else {
            rows.push({ RowType: "empty", RowKey: "e-" + job.Id, job });
        }
    }

    return rows;
});

const rowDisplayLabel = (option) => {
    if (!option) return "";

    if (option.RowType === "header") {
        return headerLabel(option.job);
    }

    if (option.RowType === "ticket") {
        const t = option.ticket;
        const title = t.RequestTitle || t.Description || "No title";
        return `${t.Id} | ${ticketStatusLabel(t.Status)} - ${title}`;
    }

    return "No linked WorkOrder tickets";
};

const rowValueKey = (option) => option?.RowKey || "";

// Only the "no tickets" placeholder rows are non-selectable. The job/header
// rows can now be selected; selecting a job sets the work-order ticket to null.
const isRowNotSelectable = (option) => option?.RowType === "empty";

// Applies the search text at the Request/job level so a group's header and
// all of its ticket rows stay together (either the whole group is shown, or
// none of it) instead of the default per-row filtering breaking groups apart.
const displayOptions = computed(() => {
    const term = searchText.value;
    if (!term) {
        return groupedOptions.value;
    }

    const rows = [];
    let currentJobId;
    let currentJobRows = [];
    let currentJobMatches = false;

    const flushGroup = () => {
        if (currentJobMatches) {
            rows.push(...currentJobRows);
        }
        currentJobRows = [];
        currentJobMatches = false;
    };

    for (const row of groupedOptions.value) {
        if (row.job.Id !== currentJobId) {
            flushGroup();
            currentJobId = row.job.Id;
        }

        currentJobRows.push(row);

        if (rowDisplayLabel(row).toLowerCase().includes(term)) {
            currentJobMatches = true;
        }
    }

    flushGroup();

    return rows;
});

const onSearchUpdate = (evt) => {
    searchText.value = (evt?.search || "").toLowerCase().trim();
};

const findRow = (key) => groupedOptions.value.find(r => r.RowKey === key);

// Re-selects the current model/workOrderId in the flattened option list
// after (re)loading. If a specific work-order ticket is set, select the
// ticket row; if only the parent job is set, select the header row.
const hydrateSelection = () => {
    if (!model.value) {
        acSelection.value = "";
        lastValidSelection.value = "";
        return;
    }

    const job = list.value.find(j => String(j.Id) === String(model.value));
    if (!job) {
        acSelection.value = "";
        lastValidSelection.value = "";
        return;
    }

    if (workOrderId.value) {
        const ticket = (job.RequestTickets || []).find(t => String(t.Id) === String(workOrderId.value));
        if (ticket) {
            const key = "t-" + ticket.Id;
            acSelection.value = key;
            lastValidSelection.value = key;
            return;
        }
    }

    // Job selected with no specific ticket - select the header row
    const key = "h-" + job.Id;
    acSelection.value = key;
    lastValidSelection.value = key;
};

const loadJobs = async () => {
    if (!props.client) {
        list.value = [];
        model.value = "";
        workOrderId.value = null;
        acSelection.value = "";
        lastValidSelection.value = "";
        return;
    }

    // Set loading flag to prevent UI flickering
    isLoadingJobs.value = true;

    try {
        let status = undefined;
        
        if (props.status?.length) {
            status = (props.status + "").split(",");
        }

        if (statusFilter.value && statusFilter.value.length > 0) {
            status = statusFilter.value;
        }

        const jobs = await Api.loadJobs({ 
            CustomerNumber: props.client, 
            Size: 9999999,
            Status: status,
            OnlyPMs: props.pmOnly,
            IncludeWorkOrders: true
        });

        // Attach the Title='Request' WorkOrder tickets linked to each Request
        for (const job of jobs) {
            job.RequestTickets = (job.WorkOrders || [])
                .filter(wo => wo.Title === "Request")
                .sort((a, b) => (a.Id || 0) - (b.Id || 0));
        }
        
        // Only update list after jobs are loaded to prevent dropdown flicker
        list.value = jobs;
        hydrateSelection();
    } finally {
        isLoadingJobs.value = false;
    }
};

// Handles a selection from the flattened dropdown. Selecting a job header
// sets the parent Request Id and clears the work-order ticket (so forms like
// PartForm see form.InitialRequest become null). Selecting a ticket sets both
// the Request Id and the specific ticket. The "no tickets" placeholder is
// non-selectable and is handled by onDisabledRowClick.
const onRowChange = (value) => {
    if (!value) {
        // Explicit clear - actually clear the selection rather than
        // reverting back to whatever was previously picked.
        lastValidSelection.value = "";
        model.value = "";
        workOrderId.value = null;
        emits("change", "");
        emits("workorder-change", null);
        return;
    }

    const row = findRow(value);

    if (!row) {
        acSelection.value = lastValidSelection.value;
        return;
    }

    lastValidSelection.value = value;
    model.value = String(row.job.Id);

    if (row.RowType === "header") {
        workOrderId.value = null;
        emits("change", model.value);
        emits("workorder-change", null);
        return;
    }

    if (row.RowType === "ticket") {
        workOrderId.value = row.ticket.Id;
        emits("change", model.value);
        emits("workorder-change", row.ticket);
        return;
    }

    // Defensive fallback for any other row type (shouldn't happen)
    acSelection.value = lastValidSelection.value;
};

// Header (Request) and "no tickets" placeholder rows can't be selected -
// give clear feedback so it doesn't look like nothing happened when clicked.
const onDisabledRowClick = (option) => {
    if (option?.RowType === "empty") {
        Toast.warning("This request has no linked WorkOrder tickets yet - link one before logging time against it");
    }
};

const editJob = async (val) => {
    if (val && workOrderId.value) {
        // A specific WorkOrder(Title='Request') ticket is selected - edit
        // that request. If it's PM-linked, open the PM form; otherwise the
        // standard RequestForm.
        try {
            const pmData = await ApiService.loadPMByRequest(val);
            if (pmData && pmData.Id) {
                isPM.value = true;
                pmForm.value = pmData;
                formTitle.value = "Edit PM";
                formVisible.value = 1;
                return;
            }
        } catch (error) {
            console.log("Not a PM-linked request");
        }

        isPM.value = false;
        formTitle.value = "Edit Request";
        try {
            form.value = await ApiService.loadWorkOrder(workOrderId.value);
        } catch (error) {
            console.error("Failed to load request ticket:", error);
            Toast.warning("Failed to load the request ticket");
            return;
        }

        formVisible.value = 1;
    } else if (val && model.value) {
        // Only the parent job is selected - open the full JobForm for it.
        Emitter.emit('edit:job', Number(model.value));
    } else {
        // New request
        isPM.value = false;
        form.value = {};

        if (props.client) {
            formTitle.value = "New Request";
            form.value.Customer = props.client;
        }

        formVisible.value = 1;
    }
};

unwatch.push(watch(() => props.client, async (nv, ov) => {
    await loadJobs();
    // Job status store is already loaded in onMounted, no need to reload on every client change
}, {
    immediate: true
}));

unwatch.push(watch(() => statusFilter.value, async (nv, ov) => {
    statusFilterWatchCount++;
    if (statusFilterWatchCount % 50 === 0) {
        console.warn(`⚠️ JobAc statusFilter watcher fired ${statusFilterWatchCount} times! INFINITE LOOP!`, nv);
    }
    console.debug(`🔄 JobAc statusFilter watch (call #${statusFilterWatchCount}):`, nv, 'old:', ov);
    
    // Don't reload from API, just filter the existing list
    // The filteredList computed property handles this
    console.debug(`📡 JobAc emitting status-change:`, nv);
    emits("status-change", nv);

    // Persist filter per user
    if (filterInitialized && userId.value) {
        try {
            const val = nv && nv.length > 0 ? JSON.stringify(nv) : null;
            await OptionsService.set(JOBAC_FILTER_KEY, val, 'Technician', String(userId.value));
        } catch (e) {
            console.warn('Failed to save JobAc filter preference', e);
        }
    }
}));

const formDismiss = () => {
    formVisible.value = 0;
};

const formSave = async (rec) => {
    if (isPM.value) {
        // Save as PM
        await ApiService.savePM(pmForm.value);
    } else {
        // Save the WorkOrder(Title='Request') ticket
        await ApiService.saveWorkOrder(form.value);
    }
    await loadJobs();
    formVisible.value = 0;
};

onMounted(async () => {
    console.debug('🎬 JobAc onMounted START');
    
    await jobStatusStore.load();
    console.debug('✅ JobStatusStore loaded');

    // Load saved filter preference for this user
    if (userId.value) {
        try {
            const saved = await OptionsService.get(JOBAC_FILTER_KEY, 'Technician', String(userId.value));
            if (saved) {
                const parsed = JSON.parse(saved);
                if (Array.isArray(parsed) && parsed.length > 0) {
                    statusFilter.value = parsed.map(Number);
                }
            }
        } catch (e) {
            console.warn('Failed to load JobAc filter preference', e);
        }
    }
    filterInitialized = true;
    console.debug('✅ JobAc filter initialized, saved filter applied:', statusFilter.value);
    console.warn('🎬 JobAc onMounted END');
});

onUnmounted(() => {
    unwatch.forEach((val) => {
        val();
    });
});

</script>

<style scoped>
.jobac-row {
    white-space: normal;
}

.jobac-row-header {
    background-color: rgba(0, 0, 0, 0.03);
    cursor: default;
}

.jobac-row-ticket {
    display: flex;
    align-items: flex-start;
    padding-left: 1.5rem;
}

.jobac-row-empty {
    padding-left: 1.5rem;
    cursor: default;
}

.jobac-status-dot {
    display: inline-block;
    width: 10px;
    height: 10px;
    min-width: 10px;
    border-radius: 50%;
    margin-top: 4px;
}

.jobac-ticket-text {
    flex: 1;
}

.jobac-ticket-title {
    line-height: 1.2;
}

.jobac-ticket-sub {
    font-size: 0.8em;
    line-height: 1.2;
}
</style>